import React, { type FC } from 'react';
import Image from '@atlaskit/image';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import atlassianIntelligenceDark from './assets/atlassian-intelligence-dark.svg';
import atlassianIntelligenceLight from './assets/atlassian-intelligence-light.svg';
import flowBuilderDark from './assets/flow-builder-dark.svg';
import flowBuilderLight from './assets/flow-builder-light.svg';
import intentTemplatesDark from './assets/intent-templates-dark.svg';
import intentTemplatesLight from './assets/intent-templates-light.svg';
import multipleChannelDark from './assets/multiple-channel-dark.svg';
import multipleChannelLight from './assets/multiple-channel-light.svg';
import { DetailSection } from './detail-section';
import messages from './messages';

const multipleChannelImageHeight = '218px';
const flowBuilderImageHeight = '231px';
const intentTemplateImageHeight = '257px';
const atlassianIntelligenceImageHeight = '254px';

export const Details: FC = () => {
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.800">
			<>
				<DetailSection
					headerMessage={messages.multipleChannelHeading}
					subheaderMessage={
						fg('virtual_agent_rebrand_to_virtual_service_agent')
							? messages.multipleChannelSubheadingV2
							: messages.multipleChannelSubheading
					}
					buttonMessage={
						fg('virtual_agent_rebrand_to_virtual_service_agent')
							? messages.multipleChannelButtonV2
							: messages.multipleChannelButton
					}
					articleId="3Il0EsHHL4CC43jddfd6dF"
					fallbackLink="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-your-virtual-agent/"
					image={
						<Image
							src={multipleChannelLight}
							srcDark={multipleChannelDark}
							alt={formatMessage(messages.multipleChannelImageAltText)}
							height={multipleChannelImageHeight}
						/>
					}
				/>

				<Box as="hr" xcss={horizontalLineStyles} />
			</>
			<DetailSection
				headerMessage={messages.flowBuilderHeading}
				subheaderMessage={messages.flowBuilderSubheading}
				buttonMessage={messages.flowBuilderButton}
				articleId="2tRHS5Wu3lb5mhEAuG72MM"
				fallbackLink="https://support.atlassian.com/jira-service-management-cloud/docs/create-or-edit-a-conversation-flow/"
				image={
					<Image
						src={flowBuilderLight}
						srcDark={flowBuilderDark}
						alt={formatMessage(messages.flowBuilderImageAltText)}
						height={flowBuilderImageHeight}
					/>
				}
			/>
			<Box as="hr" xcss={horizontalLineStyles} />
			<DetailSection
				headerMessage={messages.intentTemplatesHeading}
				subheaderMessage={messages.intentTemplatesSubheading}
				buttonMessage={messages.intentTemplatesButton}
				articleId="3EYl6cxAyyBeai11FeI8Se"
				fallbackLink="https://support.atlassian.com/jira-service-management-cloud/docs/create-edit-or-delete-an-intent/"
				image={
					<Image
						src={intentTemplatesLight}
						srcDark={intentTemplatesDark}
						alt={formatMessage(messages.intentTemplatesImageAltText)}
						height={intentTemplateImageHeight}
					/>
				}
			/>
			<Box as="hr" xcss={horizontalLineStyles} />
			<DetailSection
				headerMessage={messages.atlassianIntelligenceHeading}
				subheaderMessage={
					fg('virtual_agent_rebrand_to_virtual_service_agent')
						? messages.atlassianIntelligenceSubheadingV2
						: messages.atlassianIntelligenceSubheading
				}
				buttonMessage={messages.atlassianIntelligenceButton}
				articleId="2welozDvy1wPNqGB7fbNTM"
				fallbackLink="https://support.atlassian.com/jira-service-management-cloud/docs/activate-or-deactivate-ai-answers-in-the-virtual-agent/"
				image={
					<Image
						src={atlassianIntelligenceLight}
						srcDark={atlassianIntelligenceDark}
						alt={formatMessage(messages.atlassianIntelligenceImageAltText)}
						height={atlassianIntelligenceImageHeight}
					/>
				}
			/>
		</Stack>
	);
};

const horizontalLineStyles = xcss({
	height: '1px',
	marginInline: 'auto',
	borderTop: '1px solid',
	borderColor: 'color.border',
	maxWidth: '935px',
	width: '100%',
});
